import React, { FC, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Typography,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import { FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useContext } from 'react';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import RichTextEditor from 'components/release-notes/rich-text-editor/rich-text-editor.component';
import { translateReleaseNoteTemplate } from 'apollo/templates/release-notes';
import { ReleaseNoteOrganizationType } from 'models/release-note';

export type SupportedLanguage = {
    code: string;
    label: string;
    isMain: boolean;
};

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
    { code: 'en', label: 'English', isMain: true },
    { code: 'sv', label: 'Svenska', isMain: false },
    { code: 'no', label: 'Norsk', isMain: false },
    { code: 'fi', label: 'Suomi', isMain: false },
    { code: 'da', label: 'Dansk', isMain: false },
];

export type TranslationContentMap = Record<string, string>;

export type ReleaseNoteFormValues = {
    organizationType: ReleaseNoteOrganizationType;
    translations: TranslationContentMap;
    isPublished: boolean;
};

type Props = {
    formik: FormikProps<ReleaseNoteFormValues>;
    versionDisplay: string;
    disableVersionChange?: boolean;
    onTranslatingChange?: (isTranslating: boolean) => void;
};

const ReleaseNoteForm: FC<Props> = ({ formik, versionDisplay, onTranslatingChange }) => {
    const { t: translate } = useTranslation(['release-notes', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [activeLanguageTab, setActiveLanguageTab] = useState('en');
    const [isTranslating, setIsTranslating] = useState(false);

    const [translateReleaseNote] = useMutation<{ translateReleaseNote: string }>(translateReleaseNoteTemplate, {
        onError: (error) => showBoundary(error),
    });

    const handleTranslationContentChange = (languageCode: string, content: string) => {
        formik.setFieldValue('translations', {
            ...formik.values.translations,
            [languageCode]: content,
        });
    };

    const handleTranslateAll = async () => {
        const englishContent = formik.values.translations['en'];
        const nonEnglishLanguages = SUPPORTED_LANGUAGES.filter(
            (language) =>
                !language.isMain && !(formik.values.translations[language.code] || '').replace(/<[^>]*>/g, '').trim(),
        );

        setIsTranslating(true);
        onTranslatingChange?.(true);
        try {
            const translationResults = await Promise.all(
                nonEnglishLanguages.map((language) =>
                    translateReleaseNote({
                        variables: { content: englishContent, targetLanguage: language.code },
                    }),
                ),
            );

            const updatedTranslations = { ...formik.values.translations };
            nonEnglishLanguages.forEach((language, languageIndex) => {
                const translated = translationResults[languageIndex]?.data?.translateReleaseNote;
                if (translated) updatedTranslations[language.code] = translated;
            });

            formik.setFieldValue('translations', updatedTranslations);
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('release-notes:TRANSLATE_SUCCESS'),
            });
        } finally {
            setIsTranslating(false);
            onTranslatingChange?.(false);
        }
    };

    const englishContentHasText = Boolean((formik.values.translations['en'] || '').replace(/<[^>]*>/g, '').trim());

    const translationsError = typeof formik.errors.translations === 'string' ? formik.errors.translations : undefined;

    return (
        <Box display="flex" flexDirection="column" gap={3}>
            <Box display="flex" gap={4} flexWrap="wrap" alignItems="flex-start">
                <FormControl>
                    <FormLabel>{translate('release-notes:VERSION')}</FormLabel>
                    <Typography variant="body1" fontWeight={600} mt={1}>
                        {versionDisplay}
                    </Typography>
                </FormControl>

                <FormControl>
                    <FormLabel>{translate('release-notes:ORGANIZATION_TYPE')}</FormLabel>
                    <RadioGroup
                        row
                        value={formik.values.organizationType}
                        onChange={(event) =>
                            formik.setFieldValue('organizationType', event.target.value as ReleaseNoteOrganizationType)
                        }
                    >
                        <FormControlLabel
                            value={ReleaseNoteOrganizationType.all}
                            control={<Radio />}
                            label={translate('release-notes:ORG_TYPE_ALL')}
                        />
                        <FormControlLabel
                            value={ReleaseNoteOrganizationType.clinic}
                            control={<Radio />}
                            label={translate('release-notes:ORG_TYPE_CLINIC')}
                        />
                        <FormControlLabel
                            value={ReleaseNoteOrganizationType.lab}
                            control={<Radio />}
                            label={translate('release-notes:ORG_TYPE_LAB')}
                        />
                    </RadioGroup>
                </FormControl>
            </Box>

            <Divider />

            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle1" fontWeight="medium">
                        {translate('release-notes:RELEASE_NOTE_CONTENT')}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={isTranslating ? <CircularProgress size={16} /> : <TranslateIcon fontSize="small" />}
                        onClick={handleTranslateAll}
                        disabled={!englishContentHasText || isTranslating}
                    >
                        {isTranslating
                            ? translate('release-notes:TRANSLATING')
                            : translate('release-notes:TRANSLATE_TO_ALL_LANGUAGES')}
                    </Button>
                </Box>

                <FormControl size="small" sx={{ mb: 1.5, minWidth: 180 }}>
                    <InputLabel id="language-select-label">{translate('release-notes:LANGUAGE')}</InputLabel>
                    <Select
                        labelId="language-select-label"
                        value={activeLanguageTab}
                        label={translate('release-notes:LANGUAGE')}
                        onChange={(event) => setActiveLanguageTab(event.target.value)}
                    >
                        {SUPPORTED_LANGUAGES.map((language) => (
                            <MenuItem key={language.code} value={language.code}>
                                {language.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {SUPPORTED_LANGUAGES.filter((language) => language.code === activeLanguageTab).map((language) => (
                    <RichTextEditor
                        key={language.code}
                        value={formik.values.translations[language.code]}
                        onChange={(content) => handleTranslationContentChange(language.code, content)}
                        placeholder={
                            language.isMain
                                ? translate('release-notes:EDITOR_PLACEHOLDER_MAIN')
                                : translate('release-notes:EDITOR_PLACEHOLDER_OTHER', {
                                      language: language.label,
                                  })
                        }
                        minHeight={200}
                    />
                ))}

                {translationsError && (
                    <FormHelperText error sx={{ mt: 0.5 }}>
                        {translationsError}
                    </FormHelperText>
                )}
            </Box>
        </Box>
    );
};

export default ReleaseNoteForm;
